import React from 'react';
import {
  glassTransitions,
  GlassVariantKey,
  glassVariants,
} from '@/styles/liquidGlass';
import { Card, CardProps, styled, useTheme } from '@mui/material';

interface GlassCardProps extends Omit<CardProps, 'elevation'> {
  variant?: GlassVariantKey;
  elevation?: number;
  hoverable?: boolean;
  animated?: boolean;
}

const StyledGlassCard = styled(Card, {
  shouldForwardProp: (prop) =>
    !['variant', 'elevation', 'hoverable', 'animated'].includes(prop as string),
})<GlassCardProps>(
  ({
    theme,
    variant = 'medium',
    elevation = 2,
    hoverable = true,
    animated = true,
  }) => {
    const glassStyle = glassVariants[variant];

    if (!glassStyle) {
      return {
        background: 'transparent',
        border: 'none',
        backdropFilter: 'none',
        boxShadow: 'none',
        borderRadius: theme.spacing(2),
      };
    }
    return {
      background: glassStyle.background,
      border: glassStyle.border,
      backdropFilter: glassStyle.backdropFilter,
      boxShadow: glassStyle.boxShadow,
      borderRadius: theme.spacing(2),
      transition: animated ? glassTransitions.smooth : 'none',
      transform: 'translateZ(0)', // Enable hardware acceleration

      ...(hoverable && {
        '&:hover': {
          background: glassStyle.hoverBackground || glassStyle.background,
          boxShadow: glassStyle.hoverBoxShadow || glassStyle.boxShadow,
          transform: 'translateY(-2px)',
        },
      }),

      // Ensure text remains readable
      '& *': {
        color: 'inherit',
      },

      // Glass effect for children
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 'inherit',
        background:
          'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        pointerEvents: 'none',
        zIndex: 1,
      },

      // Ensure content is above glass overlay
      '& > *': {
        position: 'relative',
        zIndex: 2,
      },
    };
  }
);

export const GlassCard: React.FC<GlassCardProps> = ({
  variant = 'medium',
  elevation,
  hoverable = true,
  animated = true,
  children,
  sx,
  ...props
}) => {
  return (
    <StyledGlassCard
      variant={variant}
      elevation={elevation}
      hoverable={hoverable}
      animated={animated}
      sx={sx}
      {...props}
    >
      {children}
    </StyledGlassCard>
  );
};

export default GlassCard;
